import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom'; // Added Link
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { useNotification } from '../../components/ui/Notification';
import { db } from '../../firebase';
import { collection, doc, onSnapshot, query, where, limit, getDocs, updateDoc, runTransaction, serverTimestamp, getDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { Button, Container, Row, Col, Card, ListGroup, Alert, Image, Form, FormCheck } from 'react-bootstrap'; // Ensured all are here
import { getUserDeliveries, addDeliveryAddress, updateDeliveryAddress } from '../../utils/deliveryUtils';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { createLogger } from '../../utils/logger';
import {
  discountDealsForType,
  quantityForType,
  buildInventoryDecrementFields,
} from '../../utils/inventoryPricing';

const logger = createLogger('CheckoutPage');

const EMPLOYEE_NOTIFICATION_URL =
  process.env.REACT_APP_EMPLOYEE_NOTIFICATION_FUNCTION_URL ||
  'https://us-central1-pack-my-cart-dev.cloudfunctions.net/notifyEmployeesNewOrderHttp';

export default function CheckoutPage() {
  const { hardwareId, cartInstanceId } = useParams();
  const supermarketId = hardwareId;
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [cartInstance, setCartInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [productImages, setProductImages] = useState({}); // { [productId]: imageUrl }
  const [productDetails, setProductDetails] = useState({}); // { [productId]: productData }
  const [inventoryDetails, setInventoryDetails] = useState({}); // { [productId]: inventoryData }
  const [supermarket, setSupermarket] = useState(null);
  const [deliveryOption, setDeliveryOption] = useState('pickup'); // 'pickup' or 'delivery'
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [deliveryAddressConfirmed, setDeliveryAddressConfirmed] = useState(false);
  const [userDeliveries, setUserDeliveries] = useState([]);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
  const [paymentType, setPaymentType] = useState('confirmation_fee_only'); // 'confirmation_fee_only' or 'full'

  const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 0;
  const gatewayPaymentType = process.env.REACT_APP_PAYMENT_TYPE || 'wipay';
  const wipayResponseUrl = process.env.REACT_APP_WIPAY_RESPONSE_URL || 'http://localhost:3000/payment/response';
  const wipayAccountNumber = process.env.REACT_APP_WIPAY_ACCOUNT_NUMBER || '1234567890';
  const wipayEnvironment = process.env.REACT_APP_WIPAY_ENVIRONMENT || 'sandbox';
  const wipayFeeStructure = process.env.REACT_APP_WIPAY_FEE_STRUCTURE || 'merchant_absorb';
  // Set REACT_APP_SIMULATED_PAYMENT_FAILS=true to simulate payment failures in simulated mode
  const simulatedPaymentFails = process.env.REACT_APP_SIMULATED_PAYMENT_FAILS === 'true';

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/carts", label: "Cart" },
    { label: "Checkout Confirmation" }
  ];

  /**
   * Generate a WiPay-compatible order ID (max 13 characters)
   * WiPay has a 13-character limit for order IDs, but our cart instance IDs 
   * are in the format "cart_instance_<timestamp>" which is 27+ characters.
   * 
   * This function creates a shortened, unique order ID while maintaining traceability
   * through the Transactions collection where we store both the original cartInstanceId
   * and the shortened wipayOrderId.
   * 
   * Format: PMC<7-digit-timestamp><3-random-chars> = 13 characters
   * Example: PMC7200000XYZ (PMC prefix + last 7 digits of timestamp + 3 random chars)
   * 
   * @param {string} cartInstanceId - The original cart instance ID (e.g., "cart_instance_1704067200000")
   * @returns {string} A 13-character unique order ID suitable for WiPay
   */
  const generateWipayOrderId = useCallback((cartInstanceId) => {
    // Extract timestamp from cart_instance_<timestamp> format
    const timestamp = cartInstanceId.replace('cart_instance_', '');
    
    // Take last 7 digits of timestamp for uniqueness (handles timestamps of varying lengths)
    const shortTimestamp = timestamp.slice(-7);
    
    // Add a prefix to identify it as PackMyCart order (PMC = 3 chars)
    // Add 3 random alphanumeric characters for additional uniqueness
    const randomChars = Math.random().toString(36).substring(2, 5).toUpperCase();
    
    // Construct the order ID: PMC + 7-digit timestamp + 3 random chars = 13 chars
    const wipayOrderId = `PMC${shortTimestamp}${randomChars}`;
    
    // Safety check: ensure it's exactly 13 characters or less
    const finalOrderId = wipayOrderId.substring(0, 13);
    
    logger.debug('[CheckoutPage] Generated WiPay Order ID:', finalOrderId, 'from cart:', cartInstanceId);
    
    return finalOrderId;
  }, []);

  // Function to calculate deal price for a product
  const calculateDealPrice = useCallback((productId, quantity, regularPrice, productTypeId) => {
    const inventoryData = inventoryDetails[productId];
    const deals = inventoryData ? discountDealsForType(inventoryData, productTypeId || null) : [];
    if (!inventoryData || deals.length === 0) {
      return { price: regularPrice * quantity, savings: 0, dealApplied: false };
    }

    // Find the best deal for this quantity
    let bestDeal = null;
    let bestSavings = 0;

    deals.forEach(deal => {
      if (quantity >= deal.quantity) {
        const dealPrice = deal.price;
        const regularTotal = regularPrice * deal.quantity;
        const savings = regularTotal - dealPrice;
        
        if (savings > bestSavings) {
          bestSavings = savings;
          bestDeal = deal;
        }
      }
    });

    if (bestDeal) {
      // Calculate how many deal units we can apply
      const dealUnits = Math.floor(quantity / bestDeal.quantity);
      const remainingItems = quantity % bestDeal.quantity;
      
      const dealTotal = dealUnits * bestDeal.price;
      const remainingTotal = remainingItems * regularPrice;
      const totalPrice = dealTotal + remainingTotal;
      
      const totalSavings = dealUnits * bestSavings;
      
      return {
        price: totalPrice,
        savings: totalSavings,
        dealApplied: true,
        dealInfo: {
          dealQuantity: bestDeal.quantity,
          dealPrice: bestDeal.price,
          dealUnits: dealUnits,
          remainingItems: remainingItems
        }
      };
    }

    return { price: regularPrice * quantity, savings: 0, dealApplied: false };
  }, [inventoryDetails]);

  // Fetch supermarket details and user delivery addresses
  useEffect(() => {
    const fetchSupermarketAndUserData = async () => {
      if (!currentUser || !supermarketId) return;

      try {
        // Fetch supermarket details
        const supermarketDoc = await getDoc(doc(db, 'Supermarkets', supermarketId));
        if (supermarketDoc.exists) {
          const supermarketData = { id: supermarketDoc.id, ...supermarketDoc.data() };
          setSupermarket(supermarketData);
          logger.debug('[CheckoutPage] Supermarket data loaded:', {
            id: supermarketDoc.id,
            name: supermarketData.display_name,
            full_payment_enabled: supermarketData.full_payment_enabled,
            delivery_enabled: supermarketData.delivery_enabled
          });
        } else {
          logger.warn('[CheckoutPage] Supermarket not found:', supermarketId);
        }

        // Fetch user delivery addresses from Deliveries collection
        const deliveries = await getUserDeliveries(currentUser.uid);
        setUserDeliveries(deliveries);
        
        // Set delivery address and option if user has saved addresses
        if (deliveries.length > 0) {
          // First try to find a default delivery
          const defaultDelivery = deliveries.find(d => d.isDefault);
          
          if (defaultDelivery) {
            // Use the default delivery
            setSelectedDeliveryId(defaultDelivery.id);
            setDeliveryAddress(defaultDelivery.address);
            setDeliveryPhone(defaultDelivery.phone || '');
          } else {
            // If no default, use the first available delivery
            const firstDelivery = deliveries[0];
            setSelectedDeliveryId(firstDelivery.id);
            setDeliveryAddress(firstDelivery.address);
            setDeliveryPhone(firstDelivery.phone || '');
          }
          
          // Auto-set delivery option to 'delivery' if supermarket supports delivery
          if (supermarketDoc.exists && supermarketDoc.data().delivery_enabled) {
            setDeliveryOption('delivery');
            setDeliveryAddressConfirmed(true);
          }
        }

      } catch (error) {
        logger.error('Error fetching supermarket or delivery data:', error);
        setUserDeliveries([]);
      }
    };

    fetchSupermarketAndUserData();
  }, [currentUser, supermarketId]);

  useEffect(() => {
    if (currentUser && supermarketId && cartInstanceId) {
      const cartRef = doc(db, 'Carts', currentUser.uid);
      const unsubscribe = onSnapshot(cartRef,
        (docSnapshot) => {
          if (docSnapshot.exists) {
            const cartData = docSnapshot.data();
            if (cartData[supermarketId] && 
                cartData[supermarketId][cartInstanceId] && 
                cartData[supermarketId][cartInstanceId].products && 
                cartData[supermarketId][cartInstanceId].products.length > 0 &&
                cartData[supermarketId][cartInstanceId].status === 'active') {
              setCartInstance(cartData[supermarketId][cartInstanceId]);
            } else {
              setError(`This cart instance (ID: ${cartInstanceId.replace('cart_instance_','')}) is empty, not found, or not active.`);
              setCartInstance(null);
            }
          } else {
            setError('No cart found for this user.');
            setCartInstance(null);
          }
          setLoading(false);
        },
        (_err) => {
          setError('Failed to load cart details for checkout.');
          setLoading(false);
        }
      );
      return () => unsubscribe();
    } else {
      if (!currentUser) setError('Please log in to proceed to checkout.');
      if (!supermarketId || !cartInstanceId) setError('Supermarket or Cart Instance ID is missing.');
      setLoading(false);
    }
  }, [currentUser, supermarketId, cartInstanceId]);

  // Function to fetch product details and images
  const fetchProductDetails = useCallback(async (productId) => {
    if (productDetails[productId]) return; // Already fetched
    
    try {
      const productDoc = await getDoc(doc(db, 'Products', productId));
      if (productDoc.exists()) {
        const productData = productDoc.data();
        setProductDetails(prev => ({ ...prev, [productId]: productData }));
        
        // Fetch product image if available
        if (productData.media && productData.media.length > 0 && productData.media[0].url) {
          try {
            const storage = getStorage();
            const imageRef = ref(storage, productData.media[0].url);
            const imageUrl = await getDownloadURL(imageRef);
            setProductImages(prev => ({ ...prev, [productId]: imageUrl }));
          } catch (imageError) {
            logger.warn(`Failed to load image for product ${productId}:`, imageError);
          }
        }
      }
    } catch (error) {
      logger.error(`Failed to fetch product details for ${productId}:`, error);
    }
  }, [productDetails]);

  // Function to fetch inventory details for deals
  const fetchInventoryDetails = useCallback(async (productId) => {
    if (inventoryDetails[productId]) return; // Already fetched
    
    try {
      const inventoryRef = collection(db, 'Inventory');
      const inventoryQuery = query(
        inventoryRef,
        where('product_id', '==', productId),
        where('supermarket_id', '==', supermarketId),
        limit(1)
      );
      
      const inventorySnapshot = await getDocs(inventoryQuery);
      if (!inventorySnapshot.empty) {
        const inventoryData = inventorySnapshot.docs[0].data();
        setInventoryDetails(prev => ({ ...prev, [productId]: inventoryData }));
      }
    } catch (error) {
      logger.error(`Failed to fetch inventory details for ${productId}:`, error);
    }
  }, [inventoryDetails, supermarketId]);

  // Effect to fetch product details when cart instance changes
  useEffect(() => {
    if (!cartInstance || !cartInstance.products) return;
    
    // Fetch details for all products in the cart
    cartInstance.products.forEach(product => {
      if (product.productId) {
        fetchProductDetails(product.productId);
        fetchInventoryDetails(product.productId);
      }
    });
  }, [cartInstance, fetchProductDetails, fetchInventoryDetails]);

  const decrementInventory = async () => {
  // Decrement inventory for each product in the cart
  for (const product of cartInstance.products) {
    try {
      // Use supermarket_id and product_id to fetch the Inventory document
      const inventoryRef = collection(db, 'Inventory');
      const inventoryQuery = query(
        inventoryRef,
        where('supermarket_id', '==', supermarketId),
        where('product_id', '==', product.productId),
        limit(1)
      );

      const snapshot = await getDocs(inventoryQuery);
      
      if (!snapshot.empty) {
        const inventoryDoc = snapshot.docs[0];
        const data = inventoryDoc.data();
        
        const fields = buildInventoryDecrementFields(data, product.productTypeId, product.quantity || 0);
        if (Object.keys(fields).length > 0) {
          await updateDoc(inventoryDoc.ref, fields);
        }
      }
    } catch (error) {
      logger.error(`[CheckoutPage] Error updating inventory for product ${product.productId}:`, error);
      // Don't throw the error to prevent checkout from failing
      // Inventory updates are not critical for the checkout process
    }
  }
};

  const handleProceedToPayment = async () => {
  if (!currentUser || !cartInstance || confirmationFee <= 0) {
    showNotification({ type: 'error', title: 'Checkout Error', message: 'Cannot proceed: Missing user, cart instance, or fee is not set.' });
    return;
  }

  // Fetch the user's identity_id from Users collection to ensure consistency
  let userIdentityId = currentUser.uid; // Default fallback
  try {
    // Fetch the user document directly by its ID (which should be currentUser.uid)
    const userDocRef = doc(db, 'Users', currentUser.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      userIdentityId = userData.identity_id || currentUser.uid;
      logger.debug('[CheckoutPage] Found user identity_id from Users collection:', userIdentityId);
    } else {
      logger.warn('[CheckoutPage] User document not found in Users collection for ID:', currentUser.uid, '- using auth UID as fallback');
    }
  } catch (error) {
    logger.error('[CheckoutPage] Error fetching user identity_id:', error);
    // Continue with currentUser.uid as fallback
  }

  // Validate delivery option if selected
  if (deliveryOption === 'delivery') {
    if (!deliveryAddressConfirmed) {
      showNotification({ type: 'warning', title: 'Confirmation Required', message: 'Please confirm that you understand the delivery fee arrangement.' });
      return;
    }
    if (!deliveryAddress.trim()) {
      showNotification({ type: 'warning', title: 'Address Required', message: 'Please enter your delivery address.' });
      return;
    }
    if (!deliveryPhone.trim()) {
      showNotification({ type: 'warning', title: 'Phone Required', message: 'Please enter your delivery phone number.' });
      return;
    }
    
    // Save delivery address and phone if they're new or if phone is new for existing address
    if (selectedDeliveryId === null) { // No saved address selected
      await addDeliveryAddress(currentUser.uid, deliveryAddress, deliveryPhone);
    } else { // Existing address selected
      const existingDelivery = userDeliveries.find(d => d.id === selectedDeliveryId);
      if (existingDelivery) {
        const existingPhone = existingDelivery.phone || '';
        if (deliveryPhone.trim() !== existingPhone.trim()) {
          await updateDeliveryAddress(selectedDeliveryId, { phone: deliveryPhone.trim() });
        }
      }
    }
  }

  setIsProcessing(true);

  // Check inventory for all items before proceeding
  const insufficientStock = [];
  for (const product of cartInstance.products) {
    // Query Inventory for this product
    const inventoryRef = collection(db, 'Inventory');
    const inventoryQuery = query(
      inventoryRef,
      where('supermarket_id', '==', supermarketId),
      where('product_id', '==', product.productId),
      limit(1)
    );

    const snapshot = await getDocs(inventoryQuery);

    if (!snapshot.empty) {
      const inventoryDoc = snapshot.docs[0];
      const data = inventoryDoc.data();
      const availableQty = quantityForType(data, product.productTypeId || null);
      if ((product.quantity || 0) > availableQty) {
        insufficientStock.push({
          name: product.name || 'Product',
          requested: product.quantity,
          available: availableQty
        });
      }
    } else {
      insufficientStock.push({
        name: product.name || 'Product',
        requested: product.quantity,
        available: 0
      });
    }
  }

  if (insufficientStock.length > 0) {
    setIsProcessing(false);
    let msg = "Some items in your cart exceed available inventory:\n\n";
    insufficientStock.forEach(item => {
      msg += `- ${item.name}: Only ${item.available} left in stock (you requested ${item.requested})\n`;
    });
    msg += "\nPlease adjust your cart quantities before proceeding.";
    showNotification({ type: 'error', title: 'Checkout Error', message: msg });
    return;
  }

    // Calculate order subtotal with deals applied
    let orderSubtotal = 0;
    cartInstance.products.forEach(product => { 
      const dealCalculation = calculateDealPrice(
        product.productId,
        product.quantity,
        product.price || 0,
        product.productTypeId
      );
      orderSubtotal += dealCalculation.price;
    });

    // Determine payment amount based on payment type (declare once to avoid redeclaration)
    const isFullPayment = paymentType === 'full' && supermarket?.full_payment_enabled;
    const paymentAmount = isFullPayment ? (orderSubtotal + confirmationFee) : confirmationFee;

    if (gatewayPaymentType === 'simulated') {
      // Simulate payment: show success or failure after short delay and redirect to a simulated response page
      setTimeout(async () => {
        // Generate WiPay-compatible order ID (max 13 characters)
        const generatedOrderId = generateWipayOrderId(cartInstanceId);
        
        if (simulatedPaymentFails) {
          // Simulate payment failure
          setIsProcessing(false);
          
          // Create checkout data to pass to payment response page for failed payment
          const checkoutData = {
            userId: userIdentityId,
            supermarketId,
            cartInstanceId,
            supermarketName: cartInstance.supermarketName || 'N/A',
            cartItems: cartInstance.products,
            confirmationFee,
            orderSubtotal,
            paymentType: isFullPayment ? 'full' : 'confirmation_fee_only',
            deliveryOption,
            deliveryAddress: deliveryOption === 'delivery' ? deliveryAddress : null,
            deliveryPhone: deliveryOption === 'delivery' ? deliveryPhone : null
          };
          
          // Use the shortened WiPay-compatible order ID
          const simulatedOrderId = generatedOrderId;
          
          // Encode the checkout data and include it in the URL for failed payment
          const encodedData = encodeURIComponent(JSON.stringify(checkoutData));
          navigate(`/payment/response?status=failed&order_id=${encodeURIComponent(simulatedOrderId)}&data=${encodedData}&delivery_option=${encodeURIComponent(deliveryOption)}&total=${encodeURIComponent(paymentAmount)}&message=Simulated payment failure`);
        } else {
          // Simulate payment success
          await decrementInventory();

          // Update cart instance status to 'awaiting_packing' and add orderid (with data integrity protection)
          const cartRef = doc(db, 'Carts', currentUser.uid);
          await runTransaction(db, async (transaction) => {
            const cartDoc = await transaction.get(cartRef);
            if (cartDoc.exists()) {
              const cartData = cartDoc.data();
              const supermarketCartInstances = cartData[supermarketId] || {};
              if (supermarketCartInstances[cartInstanceId]) {
                const currentStatus = supermarketCartInstances[cartInstanceId].status;
                logger.debug('[CheckoutPage] Current cart status before simulated payment:', currentStatus);
                
                // Only update status if it's safe to do so (not more advanced than awaiting_packing)
                if (StatusHelpers.canUpdateToAwaitingPacking(currentStatus)) {
                  logger.debug('[CheckoutPage] Updating simulated cart status from', currentStatus, 'to awaiting_packing');
                  supermarketCartInstances[cartInstanceId].status = CART_STATUS.AWAITING_PACKING;
                  supermarketCartInstances[cartInstanceId].orderid = generatedOrderId; // Add orderid field
                  supermarketCartInstances[cartInstanceId].lastUpdatedAt = serverTimestamp();
                  transaction.update(cartRef, {
                    [supermarketId]: supermarketCartInstances,
                    lastGlobalUpdate: serverTimestamp()
                  });
                } else if (currentStatus === CART_STATUS.AWAITING_PACKING) {
                  logger.debug('[CheckoutPage] Cart already in awaiting_packing status - no update needed');
                  // Still add the orderid field if it's missing
                  if (!supermarketCartInstances[cartInstanceId].orderid) {
                    supermarketCartInstances[cartInstanceId].orderid = generatedOrderId;
                    supermarketCartInstances[cartInstanceId].lastUpdatedAt = serverTimestamp();
                    transaction.update(cartRef, {
                      [supermarketId]: supermarketCartInstances,
                      lastGlobalUpdate: serverTimestamp()
                    });
                  }
                } else {
                  logger.warn('[CheckoutPage] Cart is in more advanced status:', currentStatus, '- cannot update to awaiting_packing');
                  // Don't update status, but still proceed with payment simulation
                }
              }
            }
          });

          // Note: For simulated payments, we don't create a transaction record here either
          // The transaction record will be created by WipayResponsePage.js to maintain consistency
          // This prevents duplicate transaction records from appearing in the employee dashboard
          logger.debug('[CheckoutPage] Simulated payment - transaction record will be created by WipayResponsePage for consistency');

          setIsProcessing(false);
          
          // Create checkout data to pass to payment response page for successful payment
          const checkoutData = {
            userId: userIdentityId,
            supermarketId,
            cartInstanceId,
            supermarketName: cartInstance.supermarketName || 'N/A',
            cartItems: cartInstance.products,
            confirmationFee,
            orderSubtotal,
            paymentType: isFullPayment ? 'full' : 'confirmation_fee_only',
            deliveryOption,
            deliveryAddress: deliveryOption === 'delivery' ? deliveryAddress : null,
            deliveryPhone: deliveryOption === 'delivery' ? deliveryPhone : null
          };
          
          // Use the shortened WiPay-compatible order ID
          const simulatedOrderId = generatedOrderId;
          
          // Notify employees of new order (fire-and-forget; WipayResponsePage may also send)
          // Default to true when unset (matches Cloud Function behavior)
          const enableEmployeeNotifications =
            process.env.REACT_APP_ENABLE_EMPLOYEE_NOTIFICATIONS !== 'false';
          if (enableEmployeeNotifications) {
            let customerName = 'Customer';
            try {
              const userQuery = query(collection(db, 'Users'), where('identity_id', '==', userIdentityId), limit(1));
              const userSnap = await getDocs(userQuery);
              if (!userSnap.empty) {
                const ud = userSnap.docs[0].data();
                customerName = ud.first_name || ud.displayName || 'Customer';
              }
            } catch (e) {
              logger.warn('[CheckoutPage] Failed to fetch customer name for notification:', e);
            }
            fetch(EMPLOYEE_NOTIFICATION_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orderId: cartInstanceId,
                transactionId: simulatedOrderId,
                customerName,
                supermarketId,
              }),
            }).catch(err => logger.warn('[CheckoutPage] Employee notification failed:', err));
          }
          
          // Encode the checkout data and include it in the URL for successful payment
          const encodedData = encodeURIComponent(JSON.stringify(checkoutData));
          navigate(`/payment/response?status=success&order_id=${encodeURIComponent(simulatedOrderId)}&data=${encodedData}&delivery_option=${encodeURIComponent(deliveryOption)}&total=${encodeURIComponent(paymentAmount)}`);
        }
      }, 1500);
      return;
    }

    // Prepare WiPay payment request
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    const parameters = new URLSearchParams();
    parameters.append('account_number', wipayAccountNumber);
    parameters.append('avs', '0');
    parameters.append('country_code', 'JM');
    parameters.append('currency', 'JMD');
    
    // Generate WiPay-compatible order ID (max 13 characters)
    const wipayOrderId = generateWipayOrderId(cartInstanceId);
    
    const wipayData = {
      userId: userIdentityId,
      supermarketId,
      cartInstanceId,
      wipayOrderId, // Include the shortened order ID in the data
      supermarketName: cartInstance.supermarketName || 'N/A',
      cartItems: cartInstance.products,
      confirmationFee,
      orderSubtotal,
      paymentType: isFullPayment ? 'full' : 'confirmation_fee_only',
      deliveryOption,
      deliveryAddress: deliveryOption === 'delivery' ? deliveryAddress : null,
      deliveryPhone: deliveryOption === 'delivery' ? deliveryPhone : null,
      user_id: userIdentityId,
      supermarket_id: supermarketId,
      supermarket_name: cartInstance.supermarketName || 'N/A',
      cart_items: JSON.stringify(cartInstance.products),
      order_subtotal: orderSubtotal,
      payment_type: isFullPayment ? 'full' : 'confirmation_fee_only',
      delivery_option: deliveryOption,
      delivery_address: deliveryOption === 'delivery' ? deliveryAddress : null,
      delivery_phone: deliveryOption === 'delivery' ? deliveryPhone : null
    };
    
    parameters.append('data', JSON.stringify(wipayData));
    parameters.append('environment', wipayEnvironment);
    parameters.append('fee_structure', wipayFeeStructure);
    parameters.append('method', 'credit_card');
    // Use the shortened WiPay-compatible order ID (max 13 characters)
    parameters.append('order_id', wipayOrderId);
    parameters.append('origin', 'PackMyCart');
    parameters.append('response_url', wipayResponseUrl);
    parameters.append('total', paymentAmount.toFixed(2)); // Amount to pay now (either confirmation fee or full amount)
    
    logger.debug('[CheckoutPage] WiPay Order ID:', wipayOrderId, '(length:', wipayOrderId.length, ')');

    const options = {
      method: 'POST',
      headers: headers,
      body: parameters,
      redirect: 'follow'
    };

    try {
      const response = await fetch('https://jm.wipayfinancial.com/plugins/payments/request', options);
      const result = await response.text();
      const json = JSON.parse(result);
      if (json.url) {
        // Note: For real WiPay payments, we don't create a transaction record here
        // The transaction record will be created by WipayResponsePage.js after successful payment
        // This prevents duplicate transaction records from appearing in the employee dashboard
        // Inventory will also be decremented by WipayResponsePage.js only after successful payment
        logger.debug('[CheckoutPage] Real WiPay payment - transaction record and inventory will be handled by WipayResponsePage after successful payment');
        
        // Add orderid field to cart instance before redirecting to WiPay (status remains unchanged until payment success)
        const cartRef = doc(db, 'Carts', currentUser.uid);
        await runTransaction(db, async (transaction) => {
          const cartDoc = await transaction.get(cartRef);
          if (cartDoc.exists()) {
            const cartData = cartDoc.data();
            const supermarketCartInstances = cartData[supermarketId] || {};
            if (supermarketCartInstances[cartInstanceId]) {
              const currentStatus = supermarketCartInstances[cartInstanceId].status;
              logger.debug('[CheckoutPage] Current cart status before WiPay redirect:', currentStatus);
              
              // Only proceed if cart is in a valid state for payment processing
              if (!StatusHelpers.isInvalidForPayment(currentStatus)) {
                logger.debug('[CheckoutPage] Adding WiPay orderid to cart before redirect. Cart:', cartInstanceId, 'WiPay ID:', wipayOrderId);
                supermarketCartInstances[cartInstanceId].orderid = wipayOrderId; // Add shortened orderid field
                supermarketCartInstances[cartInstanceId].lastUpdatedAt = serverTimestamp();
                transaction.update(cartRef, {
                  [supermarketId]: supermarketCartInstances,
                  lastGlobalUpdate: serverTimestamp()
                });
              } else {
                logger.warn('[CheckoutPage] Cart is in invalid status for payment:', currentStatus, '- cannot proceed to WiPay');
                throw new Error(`Cart is in invalid status: ${StatusHelpers.getDisplayText(currentStatus)}`);
              }
            }
          }
        });
        
        window.location.href = json.url; // Redirect to WiPay payment page
      } else {
        showNotification({ type: 'error', title: 'Payment Failed', message: `Payment initiation failed: ${json.message || 'Unknown error'}` });
        setIsProcessing(false);
      }
            } catch (err) {
      showNotification({ type: 'error', title: 'Payment Error', message: `Failed to initiate payment. ${err.message}` });
      setIsProcessing(false);
    }
  };

  if (loading) return <Layout><Container className="mt-4 text-center"><p>Loading checkout...</p></Container></Layout>;
  
  return (
    <Layout>
      <Container className="mt-4">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate('/carts')} className="mb-2">
                    &larr; Back to Cart
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>

        {error && (
            <Alert variant="danger" className="mt-3">
                {error} <Link to="/carts">Return to Cart</Link>
            </Alert>
        )}

        {!error && !cartInstance && !loading && (
             <Alert variant="warning" className="mt-3">
                Cart instance not found or is not valid for checkout. <Link to="/carts">Return to Cart</Link>
            </Alert>
        )}

        {cartInstance && cartInstance.products && cartInstance.products.length > 0 && (
          <>
            <h2 className="mt-3 mb-3">
              Checkout Confirmation: {cartInstance.supermarketName || 'Supermarket'} 
              {deliveryOption === 'delivery' && ' (Delivery)'}
              (Cart: {cartInstanceId.replace('cart_instance_','')})
            </h2>
            
            {/* Delivery Option Selection */}
            {supermarket?.delivery_enabled && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-truck me-2"></i>
                    Delivery Options
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <FormCheck
                      type="radio"
                      id="pickup-option"
                      name="deliveryOption"
                      label="Pickup at Supermarket"
                      checked={deliveryOption === 'pickup'}
                      onChange={() => setDeliveryOption('pickup')}
                      className="mb-2"
                    />
                    <FormCheck
                      type="radio"
                      id="delivery-option"
                      name="deliveryOption"
                      label="Home Delivery"
                      checked={deliveryOption === 'delivery'}
                      onChange={() => setDeliveryOption('delivery')}
                      className="mb-2"
                    />
                  </div>

                  {deliveryOption === 'delivery' && (
                    <div className="border rounded p-3 bg-light">
                      <div className="mb-3">
                        <div className="alert alert-info mb-3">
                          <strong>Delivery Fee Information:</strong>
                          <br />
                          This supermarket offers delivery service. You will need to arrange the delivery fee directly with the supermarket. 
                          The fee will be determined by the supermarket based on your location and order size.
                        </div>
                        
                        {/* Supermarket Delivery Phone Number */}
                        {supermarket?.delivery_phone && (
                          <div className="alert alert-success mb-3">
                            <strong>Supermarket Delivery Contact:</strong>
                            <br />
                            <i className="fas fa-phone me-2"></i>
                            <a href={`tel:${supermarket.delivery_phone}`} className="text-decoration-none fw-bold">
                              {supermarket.delivery_phone}
                            </a>
                            <br />
                            <small className="text-muted">
                              Contact this number to arrange delivery details and fees.
                            </small>
                          </div>
                        )}
                        
                        <FormCheck
                          type="checkbox"
                          id="delivery-confirmation"
                          label="I understand that I need to arrange the delivery fee directly with the supermarket"
                          checked={deliveryAddressConfirmed}
                          onChange={(e) => setDeliveryAddressConfirmed(e.target.checked)}
                          className="mb-3"
                        />
                      </div>

                      {deliveryAddressConfirmed && (
                        <div>
                          <h6>Delivery Address</h6>
                          
                          {/* Saved Addresses and Phones */}
                           {userDeliveries.length > 0 ? (
                              <div className="mb-3">
                                <Form.Label>Select a saved address or enter a new one:</Form.Label>
                                <Form.Select 
                                  value={selectedDeliveryId || ''} 
                                  onChange={(e) => {
                                    const deliveryId = e.target.value;
                                    setSelectedDeliveryId(deliveryId);
                                    if (deliveryId) {
                                      const selectedDelivery = userDeliveries.find(d => d.id === deliveryId);
                                      if (selectedDelivery) {
                                        setDeliveryAddress(selectedDelivery.address);
                                        setDeliveryPhone(selectedDelivery.phone || '');
                                      }
                                    } else {
                                      setDeliveryAddress('');
                                      setDeliveryPhone('');
                                    }
                                  }}
                                >
                                  <option value="">Enter new address</option>
                                  {userDeliveries.map((delivery) => (
                                    <option key={delivery.id} value={delivery.id}>
                                      {delivery.address} {delivery.phone ? `(${delivery.phone})` : ''}
                                      {delivery.isDefault ? ' (Default)' : ''}
                                    </option>
                                  ))}
                                </Form.Select>
                                <small className="text-muted">
                                  Found {userDeliveries.length} saved address{userDeliveries.length > 1 ? 'es' : ''}
                                </small>
                              </div>
                            ) : null}

                          {/* New Address Input */}
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Delivery Address:</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={deliveryAddress}
                              onChange={(e) => setDeliveryAddress(e.target.value)}
                              placeholder="Enter your complete delivery address..."
                              required={deliveryOption === 'delivery'}
                              className="w-100"
                              style={{ 
                                fontSize: '1rem',
                                padding: '0.75rem',
                                borderRadius: '0.375rem',
                                resize: 'vertical'
                              }}
                            />
                            <Form.Text className="text-muted small">
                              Please provide a complete address including street, city, and any relevant landmarks.
                            </Form.Text>
                          </Form.Group>

                          {/* Phone Number Input */}
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Delivery Phone Number:</Form.Label>
                            <Form.Control
                              type="tel"
                              value={deliveryPhone}
                              onChange={(e) => setDeliveryPhone(e.target.value)}
                              placeholder="Enter your phone number for delivery contact"
                              required={deliveryOption === 'delivery'}
                              className="w-100"
                              style={{ 
                                fontSize: '1rem',
                                padding: '0.75rem',
                                borderRadius: '0.375rem'
                              }}
                            />
                            <Form.Text className="text-muted small">
                              This number will be used by the supermarket to contact you regarding delivery.
                            </Form.Text>
                          </Form.Group>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light border-bottom">
                <h4 className="mb-0 text-dark fw-bold">
                  <i className="fas fa-shopping-cart me-2 text-primary"></i>
                  Order Summary
                </h4>
              </Card.Header>
              <ListGroup variant="flush">
                {cartInstance.products.map(product => {
                  const productDetail = productDetails[product.productId];
                  const productImage = productImages[product.productId];
                  const dealCalculation = calculateDealPrice(
        product.productId,
        product.quantity,
        product.price || 0,
        product.productTypeId
      );
                  const unitPrice = product.price !== undefined ? product.price : 0;
                  
                  return (
                    <ListGroup.Item key={`${product.productId}-${product.productTypeId || ''}`} className="p-4 border-bottom">
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                        {/* Product Image */}
                        <div className="text-center text-md-start" style={{ width: '100px', height: '100px', flexShrink: 0 }}>
                          {productImage ? (
                            <Image 
                              src={productImage} 
                              alt={product.name || 'Product'} 
                              className="img-fluid rounded shadow-sm"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div 
                              className="d-flex align-items-center justify-content-center bg-light rounded shadow-sm h-100 border"
                              style={{ 
                                fontSize: '0.9rem',
                                color: '#6c757d'
                              }}
                            >
                              <i className="fas fa-image text-muted"></i>
                            </div>
                          )}
                        </div>
                        
                        {/* Product Details */}
                        <div className="flex-grow-1">
                          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-2">
                            <div className="flex-grow-1">
                              <h5 className="mb-1 fw-bold text-dark">
                                {product.name || productDetail?.display_name || 'Product'}
                              </h5>
                              {productDetail?.description && (
                                <p className="mb-1 small text-muted text-break">
                                  {productDetail.description.length > 80 
                                    ? `${productDetail.description.substring(0, 80)}...` 
                                    : productDetail.description}
                                </p>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-secondary fs-6 px-3 py-2">
                                <i className="fas fa-boxes me-1"></i>
                                Qty: {product.quantity}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Price Information */}
                        <div className="text-end text-md-end" style={{ minWidth: '120px' }}>
                          <div className="d-flex flex-column align-items-end">
                            {dealCalculation.dealApplied ? (
                              <>
                                <h5 className="mb-1 fw-bold text-success">
                                  ${dealCalculation.price.toFixed(2)}
                                </h5>
                                <small className="text-muted text-decoration-line-through">
                                  ${(unitPrice * product.quantity).toFixed(2)}
                                </small>
                                <small className="text-success fw-bold">
                                  Save ${dealCalculation.savings.toFixed(2)}
                                </small>
                              </>
                            ) : (
                              <>
                                <h5 className="mb-1 fw-bold text-success">
                                  ${dealCalculation.price.toFixed(2)}
                                </h5>
                                <small className="text-muted">
                                  ${unitPrice.toFixed(2)} each
                                </small>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              <Card.Footer className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 text-dark">Order Subtotal</h5>
                    <small className="text-muted">
                      {deliveryOption === 'delivery' ? 'Pay on delivery' : 'Pay at pickup'}
                    </small>
                  </div>
                  <div className="text-end">
                    <h4 className="mb-0 fw-bold text-primary">
                      ${(cartInstance.products.reduce((sum, p) => {
                        const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                        return sum + dealCalculation.price;
                      }, 0)).toFixed(2)}
                    </h4>
                  </div>
                </div>
              </Card.Footer>
            </Card>

            <Card className="mb-5">
              <Card.Header><h4>Payment Options</h4></Card.Header>
              <Card.Body>
                {supermarket?.full_payment_enabled ? (
                  <>
                    <div className="mb-3">
                      <FormCheck
                        type="radio"
                        id="confirmation-fee-only"
                        name="paymentType"
                        label={`Pay Confirmation Fee Only ($${confirmationFee.toFixed(2)})`}
                        checked={paymentType === 'confirmation_fee_only'}
                        onChange={() => setPaymentType('confirmation_fee_only')}
                        className="mb-2"
                      />
                      <Form.Text className="text-muted small d-block mb-3">
                        Pay the confirmation fee now. Order subtotal of ${(cartInstance.products.reduce((sum, p) => {
                          const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                          return sum + dealCalculation.price;
                        }, 0)).toFixed(2)} will be due {deliveryOption === 'delivery' ? 'upon delivery' : 'at the supermarket upon pickup'}.
                      </Form.Text>
                      <FormCheck
                        type="radio"
                        id="full-payment"
                        name="paymentType"
                        label={`Pay Full Amount ($${(cartInstance.products.reduce((sum, p) => {
                          const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                          return sum + dealCalculation.price;
                        }, 0) + confirmationFee).toFixed(2)})`}
                        checked={paymentType === 'full'}
                        onChange={() => setPaymentType('full')}
                        className="mb-2"
                      />
                      <Form.Text className="text-muted small d-block mb-3">
                        Pay the full order total now. The confirmation fee (${confirmationFee.toFixed(2)}) will be collected as a processing fee.
                      </Form.Text>
                    </div>
                    <div className="mb-3 p-3 bg-light rounded">
                      <p className="mb-1"><strong>Order Subtotal:</strong> ${(cartInstance.products.reduce((sum, p) => {
                        const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                        return sum + dealCalculation.price;
                      }, 0)).toFixed(2)}</p>
                      <p className="mb-1"><strong>Confirmation Fee:</strong> ${confirmationFee.toFixed(2)} {paymentType === 'full' ? '(as processing fee)' : ''}</p>
                      <hr className="my-2" />
                      <p className="mb-0"><strong>Total to Pay:</strong> <span className="text-success fw-bold">${(paymentType === 'full' ? (cartInstance.products.reduce((sum, p) => {
                        const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                        return sum + dealCalculation.price;
                      }, 0) + confirmationFee) : confirmationFee).toFixed(2)}</span></p>
                    </div>
                    <Button className="btn btn-primary btn-lg w-100" onClick={handleProceedToPayment} disabled={isProcessing || confirmationFee <= 0}>
                      {isProcessing ? 'Processing...' : (confirmationFee > 0 ? `Proceed to Pay $${(paymentType === 'full' ? (cartInstance.products.reduce((sum, p) => {
                        const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                        return sum + dealCalculation.price;
                      }, 0) + confirmationFee) : confirmationFee).toFixed(2)}` : 'Fee Not Set')}
                    </Button>
                  </>
                ) : (
                  <>
                    <p>To confirm your order, please pay the non-refundable confirmation fee. The order subtotal will be due {deliveryOption === 'delivery' ? 'upon delivery' : 'at the supermarket upon pickup'}.</p>
                    <p><strong>Order Subtotal:</strong> ${(cartInstance.products.reduce((sum, p) => {
                      const dealCalculation = calculateDealPrice(p.productId, p.quantity, p.price || 0, p.productTypeId);
                      return sum + dealCalculation.price;
                    }, 0)).toFixed(2)} (Due {deliveryOption === 'delivery' ? 'on delivery' : 'at pickup'})</p>
                    <p><strong>Confirmation Fee:</strong> ${confirmationFee.toFixed(2)} (Payable now)</p>
                    <Button className="btn btn-primary btn-lg w-100" onClick={handleProceedToPayment} disabled={isProcessing || confirmationFee <= 0}>
                      {isProcessing ? 'Processing...' : (confirmationFee > 0 ? `Proceed to Pay $${confirmationFee.toFixed(2)} Fee` : 'Fee Not Set')}
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </Layout>
  );
}

// TODO: Removing zero-inventory products from all carts should be done in a Cloud Function with admin privileges.
// Frontend can only update the current user's cart due to Firestore security rules.
